import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  message,
  Space,
  InputNumber,
  Checkbox,
  DatePicker,
  Card,
  Modal,
  Row,
  Col,
  Radio,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { formatNumberInInput } from '@app/utils/utils';
import moment from 'moment';
import { FaInfoCircle } from 'react-icons/fa';
import { MdCancel, MdReceipt } from 'react-icons/md';
import { EmployeeModel } from '@app/interfaces/employee';
import { OrderModel } from '@app/interfaces/order';
import { TbCash } from 'react-icons/tb';
import { CiBank } from 'react-icons/ci';
import { getListEmployees } from '@app/api/order.api';
import UserAvatar from '@app/components/common/UserAvatar/UserAvatar';
import { DATE_FORMAT } from '@app/constants/dateFormats';

/** `name` giữ riêng chuỗi để tìm kiếm, vì `label` là node chứa avatar. */
interface EmployeeOption {
  value: number;
  name: string;
  label: React.ReactNode;
}

interface ModalProps {
  open: boolean;
  onCreate: (values: any) => void | Promise<any>;
  onCancel: () => void;
  title: React.ReactNode;
  record: OrderModel | null;
  type: string;
}

const PaymentForm: React.FC<ModalProps> = ({ open, onCreate, onCancel, title, record, type }) => {
  const [form] = Form.useForm();
  const [date, setDate] = useState(moment());
  const [employeerOptions, setEmployeerOptions] = useState<EmployeeOption[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        name: `Thanh toán đơn hàng - ${record.order_code}`,
        trans_date: moment(),
        amount: record.debit_amount < 0 ? record.debit_amount * -1 : record.debit_amount,
        employee_id: record.employee_id,
        payee_recipient_type: 'Khách hàng',
        payee_recipient_name: record.customer.name,
        recognize_income_statement: true,
      });
    }
  }, [record]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await getListEmployees();

      const options: EmployeeOption[] = (response ?? []).map((item: any) => ({
        value: item.id,
        name: item.name,
        label: <UserAvatar name={item.name} avatarUrl={item.avatar_url} size={22} />,
      }));

      setEmployeerOptions(options);
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return; // chặn double-click tránh tạo phiếu thu trùng

    let values;
    try {
      values = await form.validateFields();
    } catch (info) {
      console.log('Validate failed:', info);
      return;
    }

    const payload = {
      ...values,
      transaction_type: type,
      payee_recipient_type: 'customer',
      payee_recipient_name: record?.customer_id,
      amount: type === 'receivable' ? values.amount : -1 * values.amount,
      store_id: 1,
      order_id: record?.id,
      posted: true,
    };

    try {
      setSubmitting(true);
      await onCreate(payload);
    } catch {
      // lỗi đã được parent xử lý/hiển thị
    } finally {
      setSubmitting(false);
    }
  };

  const onDateChange = (value: any) => {
    setDate(value);
  };

  const onChangeEmployee = (value: string) => {
    form.setFieldsValue({ seller: value });
  };

  return (
    <>
      <Modal
        open={open}
        onOk={handleSubmit}
        onCancel={onCancel}
        width={1000}
        style={{ top: 20 }}
        closable={false}
        footer={[
          <Space>
            <Button onClick={handleSubmit} type="primary" loading={submitting} disabled={submitting}>
              <MdReceipt /> Lưu và post
            </Button>
            <Button onClick={onCancel} type="primary" danger disabled={submitting}>
              {' '}
              <MdCancel /> Bỏ qua
            </Button>
          </Space>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          name="form-ledger"
          initialValues={{
            trans_date: moment(),
            recognize_income_statement: true,
            remark: record?.remark,
          }}
          size="small"
        >
          <h3 className="mb-4 font-bold-weight">{title}</h3>
          <Row gutter={12}>
            <Col span={12}>
              <Card>
                <Row gutter={12}>
                  <Col span={6}>Diễn giải</Col>
                  <Col span={18}>
                    <Form.Item name="name" rules={[{ required: true, message: 'Please input name!' }]}>
                      <Input placeholder="Diễn giải" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={6}>Thời gian</Col>
                  <Col span={18}>
                    <Form.Item name="trans_date">
                      <DatePicker showToday format={DATE_FORMAT} onChange={onDateChange} value={date} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={6}>Giá trị</Col>
                  <Col span={18}>
                    <Form.Item name="amount" rules={[{ required: true, message: 'Amount must be required.' }]}>
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(value) => formatNumberInInput(Number(value))}
                        addonAfter="VND"
                        className="text-primary"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={6}>Nhân viên thu</Col>
                  <Col span={18}>
                    <Form.Item name="employee_id">
                      <Select
                        allowClear
                        showSearch
                        placeholder="Nhân viên thu"
                        onChange={onChangeEmployee}
                        filterOption={(input, option) =>
                          (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={employeerOptions}
                        suffixIcon={<UserOutlined className="text-primary" />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Row gutter={12}>
                  <Col span={6}>Đối tượng nộp</Col>
                  <Col span={18}>
                    <Form.Item name="payee_recipient_type">
                      <Input readOnly />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={6}>Tên khách hàng</Col>
                  <Col span={18}>
                    {/* Ô này chỉ để hiển thị (payload gửi customer_id), nên thay
                        input readOnly bằng avatar + tên cho dễ nhận diện.
                        Vẫn giữ Form.Item ẩn để giá trị form không đổi. */}
                    {/* Canh cao bằng các ô nhập cùng hàng để lưới không bị lệch. */}
                    <div className="tw-flex tw-min-h-[var(--control-height-sm,36px)] tw-items-center tw-px-1">
                      <UserAvatar
                        name={record?.customer?.name}
                        avatarUrl={record?.customer?.avatar_url}
                        size={26}
                      />
                    </div>
                    <Form.Item name="payee_recipient_name" hidden>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={6}></Col>
                  <Col span={18}>
                    <Form.Item name="payment_type">
                      <Radio.Group defaultValue="cash">
                        <Radio value="cash">
                          <TbCash /> Tiền mặt
                        </Radio>
                        <Radio value="bank_transfer">
                          <CiBank /> Chuyển khoản
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={6}>Ghi chú</Col>
                  <Col span={18}>
                    <Form.Item name="remark">
                      <Input.TextArea placeholder="Ghi chú" autoSize={{ maxRows: 4 }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={6}></Col>
                  <Col span={18}>
                    <Form.Item name="recognize_income_statement" valuePropName="checked">
                      <Checkbox disabled>
                        Hoạch toán vào kết quả kinh doanh{' '}
                        <FaInfoCircle title="Chọn trong trường hợp giá trị này là khoản Thu nhập thêm hoặc Chi phí vận hành, phát sinh của cửa hàng. Không chọn trong trường hợp Thu tiền hàng hoặc Trả tiền nhà cung cấp." />{' '}
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default PaymentForm;
